import { DatePipe } from "@angular/common";
import { AfterViewInit, ChangeDetectionStrategy, Component, computed, inject, signal, ViewChild } from "@angular/core";
import { RouterLink } from "@angular/router";
import {
    ButtonComponent,
    ColumnType,
    Page,
    PaginatorComponent,
    PaginatorVariant,
    ServerDataSource,
    TableComponent,
    TableFormat
} from "@sxm-content-creators/tools-ds";
import { map } from "rxjs";
import { Theme } from "../../models/theme.model";
import { ThemesApiService } from "../../services/themes.api.service";

@Component({
    selector: "app-themes",
    templateUrl: "./themes.component.html",
    styleUrl: "./themes.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [ButtonComponent, RouterLink, TableComponent, PaginatorComponent]
})
export class ThemesComponent implements AfterViewInit {
    private readonly themesService = inject(ThemesApiService);
    private readonly datePipe = new DatePipe("en-US");

    @ViewChild(PaginatorComponent) paginator!: PaginatorComponent;

    readonly paginatorVariant = PaginatorVariant.CURRENT;

    private readonly cursorMap = new Map<number, string>();

    /** Set once a load resolves, so the empty state can't flash while the first page is in flight. */
    private readonly hasLoaded = signal(false);
    private readonly isEmpty = signal(false);

    /** Swaps the table + paginator for the "No Themes Yet" panel. */
    readonly showEmptyState = computed(() => this.hasLoaded() && this.isEmpty());

    readonly dataSource = new ServerDataSource<Theme>((page: Page<Theme>) => this.loadThemes(page), {
        active: "",
        direction: ""
    });

    readonly tableFormat: TableFormat = {
        headers: [{ name: "Name" }, { name: "Theme ID" }, { name: "Last Updated" }, { name: "" }],
        columns: [
            { type: ColumnType.TEXT, firstLine: "name", display: "name" },
            { type: ColumnType.TEXT, firstLine: "id" },
            {
                type: ColumnType.TEXT,
                firstLine: "updatedAt",
                display: "last-updated",
                pipe: this.datePipe,
                pipeArgs: ["MMM d, y"]
            },
            { type: ColumnType.CUSTOM, display: "edit-action" }
        ]
    };

    // The paginator is removed from the DOM while the empty state shows, so both accesses are
    // guarded: an empty list has nothing to page through anyway.
    ngAfterViewInit(): void {
        this.dataSource.count().subscribe(count => {
            if (this.paginator) {
                this.paginator.length = count;
            }
        });

        this.paginator?.page.subscribe(event => {
            this.dataSource.pageChange(event);
        });
    }

    // Currently cursor pagination isn't supported by the tools-ds table so have to work around that a bit here.
    // Jira for adding support for it is https://jira.savagebeast.com/browse/CREATORS-26602
    private loadThemes(page: Page<Theme>) {
        const cursor = this.cursorMap.get(page.index) ?? null;
        return this.themesService.getThemes(page.size, cursor).pipe(
            map(response => {
                if (response.nextCursor) {
                    this.cursorMap.set(page.index + 1, response.nextCursor);
                }
                // Only the first page coming back empty means there are no themes at all — under
                // cursor pagination a later page can't be empty, so it must not blank the table.
                if (page.index === 0) {
                    this.isEmpty.set(response.items.length === 0);
                }
                this.hasLoaded.set(true);
                return {
                    data: response.items,
                    count: response.hasNext ? Number.MAX_SAFE_INTEGER : response.items.length
                };
            })
        );
    }
}
